/**
 * Lightweight client-side log buffer.
 * Captures console output plus window errors so we can download a session log
 * when users hit issues (or even when everything appears fine).
 */

import {
  isBenignPdfNoise,
  isBenignPdfNoiseArgs,
  isExtensionNoise,
  isExtensionNoiseArgs,
} from './suppressCanvasErrors';

export type ClientLogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

export interface ClientLogEntry {
  id: string;
  level: ClientLogLevel;
  message: string;
  data?: any[];
  timestamp: string; // ISO string
  source: 'console' | 'window-error' | 'unhandled-rejection';
}

let buffer: ClientLogEntry[] = [];
let installed = false;
let maxEntries = 500; // slightly reduced to be safer
const MAX_STRING_LENGTH = 10000; // Cap string lengths to avoid memory bloat
const MAX_DATA_SIZE_BYTES = 50000; // Cap size of individual log data objects

const randomId = () => `log-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Heuristic to estimate the size of an object in memory
 */
const estimateSize = (obj: any): number => {
  try {
    return JSON.stringify(obj).length;
  } catch {
    return 1000; // Fallback
  }
};

const toPlainData = (value: any, depth = 0) => {
  // Prevent infinite recursion and massive depth
  if (depth > 3) return '[Object]';

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack?.substring(0, 2000), // Truncate stacks
    };
  }

  if (typeof value === 'string') {
    return value.length > MAX_STRING_LENGTH ? value.substring(0, MAX_STRING_LENGTH) + '... [truncated]' : value;
  }

  if (typeof value === 'object' && value !== null) {
    // Avoid cloning massive objects like the entire DataService DB
    // Check if it looks like a large collection or the DB itself
    if (Array.isArray(value) && value.length > 100) {
      return `[Array(${value.length})]`;
    }
    
    const size = estimateSize(value);
    if (size > MAX_DATA_SIZE_BYTES) {
      return `[Large Object (~${Math.round(size / 1024)}KB)]`;
    }

    try {
      // Use a safer cloning method that handles circular references
      // but for simplicity we'll just use stringify/parse with a catch
      return JSON.parse(JSON.stringify(value));
    } catch {
      return `[Complex Object: ${value.constructor?.name || 'Object'}]`;
    }
  }

  if (typeof value === 'function') return `[function ${value.name || 'anonymous'}]`;
  return value;
};

const pushEntry = (entry: Omit<ClientLogEntry, 'id' | 'timestamp'>) => {
  // Final safety check: if buffer is getting huge despite entry limits, 
  // clear it aggressively. This shouldn't happen with the caps above.
  if (buffer.length >= maxEntries) {
    // Shift is better than slice for performance in some engines
    buffer.shift();
  }

  buffer.push({
    id: randomId(),
    timestamp: new Date().toISOString(),
    ...entry,
  });
};

export function installClientLogger(options?: { maxEntries?: number }) {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  if (options?.maxEntries && options.maxEntries > 0) {
    maxEntries = options.maxEntries;
  }

  const original = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
    debug: console.debug,
  };

  (['log', 'info', 'warn', 'error', 'debug'] as ClientLogLevel[]).forEach((level) => {
    console[level] = (...args: any[]) => {
      if (isExtensionNoiseArgs(args)) {
        original[level](...args);
        return;
      }
      if (isBenignPdfNoiseArgs(args)) {
        return;
      }

      // Suppress generated inspector noise
      if (level === 'error' || level === 'warn') {
        const firstArg = String(args[0] ?? '');
        const propName = String(args[1] ?? '');
        if (
          (firstArg.includes('does not recognize') && firstArg.includes('prop on a DOM element') && propName.startsWith('_fg')) ||
          firstArg.includes('Could not access stylesheet') ||
          firstArg.includes('cssRules')
        ) {
          original[level](...args);
          return;
        }
      }

      try {
        pushEntry({
          level,
          message: args.map((arg) => (arg instanceof Error ? arg.message : String(arg))).join(' ').substring(0, 1000),
          data: args.map(arg => toPlainData(arg)),
          source: 'console',
        });
      } catch {
        // Swallow logging failures
      }

      original[level](...args);
    };
  });

  window.addEventListener('error', (event) => {
    if (
      isExtensionNoise(event.message) ||
      isExtensionNoise(event.filename) ||
      isBenignPdfNoise(event.message)
    ) {
      return;
    }
    pushEntry({
      level: 'error',
      message: (event.message || 'Unhandled window error').substring(0, 1000),
      data: [toPlainData(event.error), { filename: event.filename, lineno: event.lineno, colno: event.colno }],
      source: 'window-error',
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = (event as PromiseRejectionEvent).reason;
    const message = reason?.message || String(reason ?? '');
    if (
      isExtensionNoise(message) ||
      isExtensionNoise(reason?.stack) ||
      isBenignPdfNoise(message) ||
      isBenignPdfNoise(reason?.stack)
    ) {
      return;
    }
    pushEntry({
      level: 'error',
      message: message.substring(0, 1000),
      data: [toPlainData(reason)],
      source: 'unhandled-rejection',
    });
  });

  // Baseline entries
  pushEntry({
    level: 'info',
    message: 'client_logger_initialized',
    data: [
      {
        url: window.location.href,
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
      },
    ],
    source: 'console',
  });
}

export function getClientLogs(): ClientLogEntry[] {
  return [...buffer];
}

export function clearClientLogs() {
  buffer = [];
}

export function clientLogsAsText(logs: ClientLogEntry[] = buffer): string {
  return logs
    .map((entry) => {
      const payload = entry.data && entry.data.length > 0 ? ` | data=${JSON.stringify(entry.data)}` : '';
      return `[${entry.timestamp}] ${entry.level.toUpperCase()} (${entry.source}) ${entry.message}${payload}`;
    })
    .join('\n');
}

export function clientLogsBlob(logs: ClientLogEntry[] = buffer): Blob {
  return new Blob([
    JSON.stringify(
      {
        capturedAt: new Date().toISOString(),
        total: logs.length,
        logs,
      },
      null,
      2
    ),
  ], { type: 'application/json' });
}

export function downloadClientLogs(filename = 'client-log.json') {
  try {
    const blob = clientLogsBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download client logs:', error);
  }
}

export function logClientEvent(message: string, data?: any, level: ClientLogLevel = 'info') {
  try {
    pushEntry({
      level,
      message: message.substring(0, 1000),
      data: data === undefined ? undefined : [toPlainData(data)],
      source: 'console',
    });
  } catch {
    // never throw during logging
  }
}
